// src/hooks/useMediaCapture.js - Hook personalizado para captura de medios
import { useState, useRef } from 'react';

export const useMediaCapture = () => {
  const [recording, setRecording] = useState(false);
  const [mediaBlob, setMediaBlob] = useState(null);
  const [mediaBlobUrl, setMediaBlobUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [processingPhoto, setProcessingPhoto] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);

  // Función para agregar marca de agua profesional
  const addWatermark = (context, width, height, locationData) => {
    const now = new Date();
    const timeString = now.toLocaleTimeString('es-PE', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
    const dateString = now.toLocaleDateString('es-PE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
    
    // Temperatura desde locationData o simulada
    const temperature = locationData?.temperature || Math.floor(Math.random() * 15) + 15;
    const tempString = `${temperature}°C`;
    
    // Icono del clima basado en código del clima (WMO)
    const getWeatherIcon = (weatherCode) => {
      // Códigos WMO: https://open-meteo.com/en/docs
      if (weatherCode === 0) return '☀️'; // Cielo despejado
      if (weatherCode >= 1 && weatherCode <= 3) return '⛅'; // Parcialmente nublado
      if (weatherCode >= 45 && weatherCode <= 48) return '🌫️'; // Niebla
      if (weatherCode >= 51 && weatherCode <= 55) return '🌦️'; // Lluvia ligera
      if (weatherCode >= 56 && weatherCode <= 57) return '🌨️'; // Lluvia helada
      if (weatherCode >= 61 && weatherCode <= 65) return '🌧️'; // Lluvia
      if (weatherCode >= 66 && weatherCode <= 67) return '🌨️'; // Lluvia helada
      if (weatherCode >= 71 && weatherCode <= 75) return '🌨️'; // Nieve
      if (weatherCode >= 77) return '🌨️'; // Granizo
      if (weatherCode >= 80 && weatherCode <= 82) return '🌦️'; // Lluvia con tormenta
      if (weatherCode >= 85 && weatherCode <= 86) return '🌨️'; // Nieve con tormenta
      if (weatherCode >= 95) return '⛈️'; // Tormenta
      if (weatherCode >= 96 && weatherCode <= 99) return '⛈️'; // Tormenta con granizo
      
      // Fallback basado en temperatura
      if (temperature >= 25) return '☀️';
      if (temperature >= 20) return '⛅';
      if (temperature >= 15) return '☁️';
      if (temperature >= 10) return '🌧️';
      return '❄️';
    };
    const weatherIcon = getWeatherIcon(locationData?.weatherCode);
    
    // Dirección desde locationData
    const address = locationData?.address || 'Ubicación no disponible';
    
    // Configuración de la marca de agua limpia
    const watermarkConfig = {
      textColor: '#ffffff',
      tempFontSize: Math.max(width * 0.045, 24), // Temperatura más grande
      normalFontSize: Math.max(width * 0.025, 14),
      lineHeight: 1.4,
      shadowColor: 'rgba(0, 0, 0, 0.8)',
      shadowBlur: 3,
      shadowOffsetX: 1,
      shadowOffsetY: 1
    };
    
    // Posición (esquina inferior derecha)
    const margin = Math.max(width * 0.03, 20);
    const x = width - margin;
    const y = height - margin;
    
    // Dibujar texto con sombra para mejor legibilidad
    context.save();
    context.shadowColor = watermarkConfig.shadowColor;
    context.shadowBlur = watermarkConfig.shadowBlur;
    context.shadowOffsetX = watermarkConfig.shadowOffsetX;
    context.shadowOffsetY = watermarkConfig.shadowOffsetY;
    
    context.fillStyle = watermarkConfig.textColor;
    context.textAlign = 'right';
    
    // Dibujar temperatura grande con icono
    context.font = `bold ${watermarkConfig.tempFontSize}px 'Segoe UI', Arial, sans-serif`;
    const tempWithIcon = `${weatherIcon} ${tempString}`;
    context.fillText(tempWithIcon, x, y - 60);
    
    // Dibujar fecha y hora
    context.font = `600 ${watermarkConfig.normalFontSize}px 'Segoe UI', Arial, sans-serif`;
    const dateTimeString = `${timeString} | ${dateString}`;
    context.fillText(dateTimeString, x, y - 35);
    
    // Dibujar dirección
    context.font = `${watermarkConfig.normalFontSize}px 'Segoe UI', Arial, sans-serif`;
    context.fillText(address, x, y - 10);
    
    context.restore();
  };

  // Función para capturar foto desde webcam con marca de agua
  const capturePhoto = (webcamRef, mediaType, setError, locationData = null) => {
    return new Promise((resolve, reject) => {
      try {
        console.log('📸 Intentando capturar foto con marca de agua...');
        
        // Activar estado de procesamiento
        setProcessingPhoto(true);
        
        if (!webcamRef || !webcamRef.current) {
          console.error('❌ Webcam ref no disponible');
          setProcessingPhoto(false);
          reject(new Error('Webcam no disponible'));
          return;
        }

        const video = webcamRef.current;
        console.log('📹 Video element:', video);
        console.log('📹 Video readyState:', video.readyState);

        // Esperar a que el video esté listo
        if (video.readyState < 2) {
          console.error('❌ Video no está listo');
          setProcessingPhoto(false);
          reject(new Error('Video no está listo para captura'));
          return;
        }

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        // Usar dimensiones del video o valores por defecto
        const width = video.videoWidth || video.clientWidth || 640;
        const height = video.videoHeight || video.clientHeight || 480;
        
        canvas.width = width;
        canvas.height = height;

        console.log('🎨 Canvas dimensions:', canvas.width, 'x', canvas.height);

        // Dibujar frame del video en el canvas
        context.drawImage(video, 0, 0, width, height);

        // Agregar marca de agua profesional
        addWatermark(context, width, height, locationData);

        // Convertir canvas a blob de forma asíncrona
        canvas.toBlob((blob) => {
          if (blob) {
            console.log('✅ Foto capturada con marca de agua exitosamente:', blob.size, 'bytes');
            setMediaBlob(blob);
            setMediaBlobUrl(URL.createObjectURL(blob));
            setProcessingPhoto(false); // Desactivar estado de procesamiento
            resolve(true);
          } else {
            console.error('❌ Error creando blob de la foto');
            setProcessingPhoto(false); // Desactivar estado de procesamiento
            setError?.('Error creando la imagen');
            reject(new Error('Error creando la imagen'));
          }
        }, 'image/jpeg', 0.9);

      } catch (error) {
        console.error('❌ Error capturando foto:', error);
        setProcessingPhoto(false); // Desactivar estado de procesamiento
        setError?.(error.message);
        reject(error);
      }
    });
  };

  // Función para iniciar grabación de video/audio
  const startRecording = (stream, mediaType, setError) => {
    return new Promise((resolve, reject) => {
      try {
        console.log('🎬 Intentando iniciar grabación...');
        console.log('📹 Stream:', stream);
        console.log('📹 MediaType:', mediaType);
        
        if (!stream) {
          console.error('❌ Stream no disponible');
          reject(new Error('Stream no disponible'));
          return;
        }

        // Limpiar estado anterior
        chunksRef.current = [];
        setRecordingTime(0);

        const options = {};
        if (mediaType === 'video') {
          // Priorizar formatos que resulten en archivos con extensión correcta
          if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')) {
            options.mimeType = 'video/webm;codecs=vp9,opus';
          } else if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')) {
            options.mimeType = 'video/webm;codecs=vp8,opus';
          } else if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('video/webm')) {
            options.mimeType = 'video/webm';
          }
        } else {
          // Para audio, priorizar formatos que se puedan convertir fácilmente a MP3
          if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
            options.mimeType = 'audio/webm;codecs=opus';
          } else if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/webm')) {
            options.mimeType = 'audio/webm';
          } else if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
            options.mimeType = 'audio/ogg;codecs=opus';
          }
        }

        // Verificar si el navegador soporta el formato
        if (options.mimeType && typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && !MediaRecorder.isTypeSupported(options.mimeType)) {
          options.mimeType = mediaType === 'video' ? 'video/webm' : 'audio/webm';
        }

        // Si aún no es soportado, usar formato por defecto
        if (options.mimeType && typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && !MediaRecorder.isTypeSupported(options.mimeType)) {
          options.mimeType = '';
        }

        console.log('🎥 Iniciando grabación con opciones:', options);
        
        const mediaRecorder = new MediaRecorder(stream, options);
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunksRef.current.push(event.data);
            console.log('📦 Chunk recibido:', event.data.size, 'bytes');
          }
        };

        mediaRecorder.onstop = () => {
          console.log('🛑 Grabación detenida, procesando...');
          const fallbackType = mediaType === 'video' ? 'video/webm' : 'audio/webm';
          const blob = new Blob(chunksRef.current, { 
            type: options.mimeType || fallbackType
          });
          
          console.log('✅ Blob creado:', blob.size, 'bytes, tipo:', blob.type);
          
          setMediaBlob(blob);
          setMediaBlobUrl(URL.createObjectURL(blob));
          setRecording(false);
          setRecordingTime(0);
          
          // Limpiar intervalos
          if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
          }
        };

        mediaRecorder.onstart = () => {
          console.log('🎬 Grabación iniciada');
          setRecording(true);
          setRecordingTime(0);
          
          // Iniciar contador de tiempo
          recordingIntervalRef.current = setInterval(() => {
            setRecordingTime(prev => prev + 1);
          }, 1000);
          
          // Resolver la promesa cuando la grabación inicie exitosamente
          resolve(true);
        };

        mediaRecorder.onerror = (event) => {
          console.error('❌ Error en grabación:', event.error);
          setError?.('Error durante la grabación: ' + event.error.message);
          setRecording(false);
          setRecordingTime(0);
          reject(new Error('Error durante la grabación: ' + event.error.message));
        };

        // Iniciar grabación
        mediaRecorder.start(1000); // Chunks cada segundo para mejor compatibilidad
        
      } catch (error) {
        console.error('❌ Error iniciando grabación:', error);
        setError?.(error.message);
        reject(error);
      }
    });
  };

  // Función para detener grabación
  const stopRecording = () => {
    try {
      if (mediaRecorderRef.current && recording) {
        console.log('🛑 Deteniendo grabación...');
        mediaRecorderRef.current.stop();
      }
    } catch (error) {
      console.error('❌ Error deteniendo grabación:', error);
    }
  };

  // Función para manejar selección de archivos
  const handleFileSelect = (file, mediaType) => {
    try {
      // Validar tipo de archivo
      const isValidType = validateFileType(file, mediaType);
      if (!isValidType) {
        return false;
      }

      // El tamaño será validado por el backend
      setSelectedFile(file);
      setMediaBlob(file);
      setMediaBlobUrl(URL.createObjectURL(file));
      
      return true;
    } catch (error) {
      console.error('❌ Error procesando archivo:', error);
      return false;
    }
  };

  // Función para validar tipo de archivo
  const validateFileType = (file, mediaType) => {
    const validTypes = {
      photo: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
      video: ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'],
      audio: ['audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/aac'],
      file: ['application/pdf', 'application/msword', 'text/plain', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
    };

    const allowedTypes = validTypes[mediaType] || [];
    return allowedTypes.includes(file.type) || file.type.startsWith('image/') || file.type.startsWith('video/') || file.type.startsWith('audio/');
  };

  // Función para resetear captura
  const resetCapture = () => {
    setRecording(false);
    setMediaBlob(null);
    setSelectedFile(null);
    setRecordingTime(0);
    setProcessingPhoto(false);
    
    // Limpiar URL del blob anterior
    if (mediaBlobUrl) {
      URL.revokeObjectURL(mediaBlobUrl);
      setMediaBlobUrl(null);
    }
    
    // Limpiar intervalos
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }
    
    // Limpiar referencias
    chunksRef.current = [];
    mediaRecorderRef.current = null;
  };

  // Función para formatear tiempo de grabación
  const formatRecordingTime = () => {
    const hours = Math.floor(recordingTime / 3600);
    const minutes = Math.floor((recordingTime % 3600) / 60);
    const seconds = recordingTime % 60;
    return `REC ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return {
    recording,
    mediaBlob,
    mediaBlobUrl,
    recordingTime,
    selectedFile,
    processingPhoto,
    capturePhoto,
    startRecording,
    stopRecording,
    handleFileSelect,
    resetCapture,
    formatRecordingTime
  };
};

